import { apiClient } from '../utils/apiClient';

/**
 * Organisations — the level above teams (plan §5, Phase 4a).
 *
 * Mirrors `teamsService.js` method-for-method and error-shape-for-error-shape.
 * The two are the same kind of thing one level apart, and a service that
 * threw differently would make every caller handle organisations specially for
 * no reason.
 *
 * Deliberately NOT wired into the sync engine. Organisations are small,
 * rarely-changing records — fetched on demand, exactly like teams.
 */
class OrganizationsService {
  async getOrganizations() {
    try {
      const response = await apiClient.get('/organizations');
      return response.data.organizations || [];
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch organizations');
    }
  }


  async createOrganization(data) {
    // Validation errors are rethrown untouched: the server returns a field-keyed
    // `errors` map and the form needs it, so flattening to a string here would
    // throw away the only part the UI can act on.
    const response = await apiClient.post('/organizations', data);
    return response.data.organization;
  }

  async updateOrganization(id, data) {
    const response = await apiClient.put(`/organizations/${id}`, data);
    return response.data.organization;
  }

  async getMembers(id) {
    try {
      const response = await apiClient.get(`/organizations/${id}/members`);
      return response.data.members || [];
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch members');
    }
  }


  async updateMemberRole(id, userId, role) {
    const response = await apiClient.put(`/organizations/${id}/members/${userId}/role`, { role });
    return response.data.member;
  }

  async removeMember(id, userId) {
    const response = await apiClient.delete(`/organizations/${id}/members/${userId}`);
    return response.data;
  }


  async getDomains(id) {
    try {
      const response = await apiClient.get(`/organizations/${id}/domains`);
      return response.data.domains || [];
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch domains');
    }
  }

  async addDomain(id, domain) {
    const response = await apiClient.post(`/organizations/${id}/domains`, { domain });
    return response.data.domain;
  }

  async verifyDomain(id, domainId) {
    const response = await apiClient.post(`/organizations/${id}/domains/${domainId}/verify`);
    return response.data.domain;
  }

  async removeDomain(id, domainId) {
    const response = await apiClient.delete(`/organizations/${id}/domains/${domainId}`);
    return response.data;
  }

  async uploadLogo(id, file) {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post(`/organizations/${id}/logo`, form);
    return response.data.organization;
  }

  async uploadBanner(id, file) {
    const form = new FormData();
    form.append('file', file);
    const response = await apiClient.post(`/organizations/${id}/banner`, form);
    return response.data.organization;
  }

  async getTeams(id) {
    try {
      const response = await apiClient.get(`/organizations/${id}/teams`);
      return response.data.teams || [];
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch teams');
    }
  }

  async attachTeam(id, teamId) {
    const response = await apiClient.post(`/organizations/${id}/teams`, { team_id: teamId });
    return response.data.team;
  }

  async detachTeam(id, teamId) {
    const response = await apiClient.delete(`/organizations/${id}/teams/${teamId}`);
    return response.data.team;
  }

  async getInvitations(id) {
    try {
      const response = await apiClient.get(`/organizations/${id}/invitations`);
      return response.data.invitations || [];
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch invitations');
    }
  }

  /**
   * Invites by email. Resolves to `{ result: 'added' | 'invited', ... }` —
   * the two are different things to be waiting for, so the caller is told
   * which happened rather than a bare success.
   */
  async invite(id, email, role = 'member') {
    const response = await apiClient.post(`/organizations/${id}/invitations`, { email, role });
    return response.data;
  }

  /** Sends the same invitation again. Keeps the existing token — revoking and
   *  re-inviting would mint a new one and silently invalidate any copy the
   *  recipient already holds. Does not extend the expiry. */
  async resendInvitation(id, invitationId) {
    const response = await apiClient.post(`/organizations/${id}/invitations/${invitationId}/resend`);
    return response.data;
  }

  async revokeInvitation(id, invitationId) {
    const response = await apiClient.delete(`/organizations/${id}/invitations/${invitationId}`);
    return response.data;
  }

  async getAudit(id) {
    try {
      const response = await apiClient.get(`/organizations/${id}/audit`);
      return response.data.entries || [];
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch the audit log');
    }
  }

  /** What this user could join right now — pending invitations and matching
   *  verified domains. Offers only; joining is always an explicit act. */
  async getJoinable() {
    try {
      const response = await apiClient.get('/organizations-joinable');
      return response.data.joinable || [];
    } catch {
      // An offer nobody can see is a missing prompt, not a broken page.
      return [];
    }
  }

  async acceptInvitation(token) {
    const response = await apiClient.post('/organization-invitations/accept', { token });
    return response.data.member;
  }

  async joinByDomain(id) {
    const response = await apiClient.post(`/organizations/${id}/join`);
    return response.data.member;
  }

  async getBilling(id) {
    try {
      const response = await apiClient.get(`/organizations/${id}/billing`);
      return response.data.billing;
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch billing');
    }
  }

  async getInvoices(id) {
    try {
      const response = await apiClient.get(`/organizations/${id}/billing/invoices`);
      return response.data.invoices || [];
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Failed to fetch invoices');
    }
  }

  async createPortalSession(id, returnUrl) {
    const response = await apiClient.post(`/organizations/${id}/billing/portal`, {
      return_url: returnUrl,
    });
    return response.data.url;
  }
}

const organizationsService = new OrganizationsService();
export default organizationsService;
